import { useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { trpc } from "@blis/trpc/react";

import { LoadingPage } from "@/components/LoadingPage";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ComboBox,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
} from "@/components/ui/combobox";
import { Description, FieldError, Label } from "@/components/ui/field";
import { ItemDetail } from "@/components/ui/list-box";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/toast";

type ConfigForm = {
  natSplitEnabled: boolean;
  parentCode: string;
  childCodes: string[];
  expandWhenValues: string[];
};

type TestSpecOption = {
  id: string;
  name: string;
  code: string;
};

type ResultOption = {
  id: string;
  value: string;
  description: string;
};

export function ITransplantConfigPanel() {
  const { data, isLoading } = trpc.integrations.get.useQuery({
    kind: "ITRANSPLANT",
  });
  const { data: testSpecs } = trpc.admin.testSpec.list.useQuery();
  const { data: textResults } = trpc.admin.testResult.list.useQuery({
    category: "TEXT",
  });
  const mutation = trpc.integrations.updateConfig.useMutation();

  // biome-ignore lint/suspicious/noExplicitAny: typed as the full union via discriminated kind
  const savedNatSplit = (data?.config as any)?.natSplit as
    | {
        parentCode?: string;
        childCodes?: string[];
        expandWhenValues?: string[];
      }
    | undefined;
  const natSplitEnabled = !!savedNatSplit?.parentCode;
  const parentCode = savedNatSplit?.parentCode ?? "";
  // Stabilize array identity across query refetches so reset() does not wipe
  // unsaved edits when contents are unchanged.
  const childCodesKey = JSON.stringify(savedNatSplit?.childCodes ?? []);
  const expandWhenKey = JSON.stringify(savedNatSplit?.expandWhenValues ?? []);
  const childCodes = useMemo(
    () => JSON.parse(childCodesKey) as string[],
    [childCodesKey],
  );
  const expandWhenValues = useMemo(
    () => JSON.parse(expandWhenKey) as string[],
    [expandWhenKey],
  );

  const form = useForm<ConfigForm>({
    defaultValues: {
      natSplitEnabled,
      parentCode,
      childCodes,
      expandWhenValues,
    },
  });
  const { control, reset, handleSubmit, setValue } = form;
  const splitOn = useWatch({ control, name: "natSplitEnabled" });
  const watchedParent = useWatch({ control, name: "parentCode" });
  const watchedChildren = useWatch({ control, name: "childCodes" });
  const watchedExpandWhen = useWatch({ control, name: "expandWhenValues" });

  useEffect(() => {
    reset({
      natSplitEnabled,
      parentCode,
      childCodes,
      expandWhenValues,
    });
  }, [natSplitEnabled, parentCode, childCodes, expandWhenValues, reset]);

  const testSpecItems = useMemo((): TestSpecOption[] => {
    const byCode = new Map<string, TestSpecOption>();
    for (const spec of testSpecs ?? []) {
      if (!spec.code) continue;
      byCode.set(spec.code, {
        id: spec.code,
        name: spec.name,
        code: spec.code,
      });
    }
    for (const code of [watchedParent, ...(watchedChildren ?? [])]) {
      if (!code || byCode.has(code)) continue;
      byCode.set(code, { id: code, name: code, code });
    }
    return Array.from(byCode.values());
  }, [testSpecs, watchedParent, watchedChildren]);

  const childSpecItems = useMemo(
    () => testSpecItems.filter((item) => item.code !== watchedParent),
    [testSpecItems, watchedParent],
  );

  const parentRefRange = useMemo(() => {
    const spec = testSpecs?.find((s) => s.code === watchedParent);
    return spec?.refRangeValue?.trim() || null;
  }, [testSpecs, watchedParent]);

  const resultOptions = useMemo((): ResultOption[] => {
    const byValue = new Map<string, ResultOption>();
    // TEXT results only — organism catalog is huge and irrelevant for NAT
    // expand-when values (NR / R / RR, etc.).
    for (const result of textResults ?? []) {
      byValue.set(result.value, {
        id: result.value,
        value: result.value,
        description: result.description,
      });
    }
    if (parentRefRange && !byValue.has(parentRefRange)) {
      byValue.set(parentRefRange, {
        id: parentRefRange,
        value: parentRefRange,
        description: "Parent reference range",
      });
    }
    for (const value of watchedExpandWhen ?? []) {
      if (!value || byValue.has(value)) continue;
      byValue.set(value, {
        id: value,
        value,
        description: value,
      });
    }
    return Array.from(byValue.values()).sort((a, b) =>
      a.value.localeCompare(b.value),
    );
  }, [textResults, parentRefRange, watchedExpandWhen]);

  // Drop the combined parent if it was previously selected as a discrim.
  useEffect(() => {
    if (!watchedParent || !watchedChildren?.includes(watchedParent)) return;
    setValue(
      "childCodes",
      watchedChildren.filter((code) => code !== watchedParent),
      { shouldDirty: true },
    );
  }, [watchedParent, watchedChildren, setValue]);

  if (isLoading) return <LoadingPage />;

  const onSubmit = async (values: ConfigForm) => {
    const parent = values.parentCode.trim();
    const children = [
      ...new Set(
        values.childCodes
          .map((code) => code.trim())
          .filter((code) => code.length > 0 && code !== parent),
      ),
    ];
    const expandWhen = values.expandWhenValues
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    if (values.natSplitEnabled) {
      if (!parent || children.length !== 3 || expandWhen.length === 0) {
        toast.error("NAT split is incomplete", {
          description:
            "Pick the combined NAT test, exactly three distinct discrim codes (HIV / HBV / HCV), and the non-reactive result value(s).",
        });
        return;
      }
    }

    try {
      await mutation.mutateAsync({
        kind: "ITRANSPLANT",
        config: {
          natSplit: values.natSplitEnabled
            ? {
                parentCode: parent,
                childCodes: children,
                expandWhenValues: expandWhen,
              }
            : undefined,
        },
      });
      // tRPC mutation wrapper already invalidates queries on success.
      toast.success("iTransplant configuration saved");
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error("Failed to save configuration", {
        description: message,
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex max-w-2xl flex-col gap-4"
    >
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1.5">
              <CardTitle>HIV / HBV / HCV NAT split</CardTitle>
              <CardDescription>
                Send a combined NAT screen as individual HIV / HBV / HCV results
                on the iTransplant payload only. Standard lab reports are
                unchanged. Codes are lab-specific — pick them below. API URL and
                tokens are configured per organization.
              </CardDescription>
            </div>
            <Controller
              control={control}
              name="natSplitEnabled"
              render={({ field }) => (
                <Switch
                  className="mt-1 shrink-0"
                  aria-label="Enable HIV / HBV / HCV NAT split"
                  isSelected={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </CardHeader>
        {splitOn ? (
          <CardContent className="flex flex-col gap-4">
            <Controller
              control={control}
              name="parentCode"
              render={({ field, fieldState: { error } }) => (
                <ComboBox
                  menuTrigger="focus"
                  selectedKey={field.value || null}
                  onSelectionChange={(key) =>
                    field.onChange(typeof key === "string" ? key : "")
                  }
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>Combined NAT test</Label>
                  <ComboBoxInput placeholder="Search tests..." />
                  <ComboBoxList items={testSpecItems}>
                    {(item) => (
                      <ComboBoxItem
                        id={item.id}
                        textValue={`${item.name} (${item.code})`}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {item.name}
                        </span>
                        <ItemDetail>{item.code}</ItemDetail>
                      </ComboBoxItem>
                    )}
                  </ComboBoxList>
                  <Description>
                    The combined HIV / HBV / HCV NAT screen.
                  </Description>
                  <FieldError>{error?.message}</FieldError>
                </ComboBox>
              )}
            />

            <Controller
              control={control}
              name="childCodes"
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-1">
                  <Label>HIV / HBV / HCV discrim tests</Label>
                  <SearchableSelect
                    selectionMode="multiple"
                    selectedKeys={new Set(field.value)}
                    onSelectionChange={(keys) => {
                      if (keys === "all") {
                        field.onChange(childSpecItems.map((item) => item.id));
                        return;
                      }
                      field.onChange(
                        Array.from(keys).filter(
                          (key): key is string =>
                            typeof key === "string" && key !== watchedParent,
                        ),
                      );
                    }}
                  >
                    <SearchableSelect.Button className="justify-between">
                      <SearchableSelect.Value
                        items={childSpecItems}
                        getKey={(item) => item.id}
                        getText={(item) => `${item.name} (${item.code})`}
                        placeholder="Select discrim tests"
                        className="flex-1 text-start"
                      >
                        {({ selectedItems }) => (
                          <span className="truncate">
                            {selectedItems.length === 0
                              ? "Select discrim tests"
                              : selectedItems
                                  .map((item) => item.code)
                                  .join(", ")}
                          </span>
                        )}
                      </SearchableSelect.Value>
                    </SearchableSelect.Button>
                    <SearchableSelect.Popover>
                      <SearchableSelect.Input placeholder="Search tests..." />
                      <SearchableSelect.List
                        shouldFocusOnHover
                        items={childSpecItems}
                      >
                        {(item) => (
                          <SearchableSelect.MultiItem
                            id={item.id}
                            textValue={`${item.name} (${item.code})`}
                          >
                            <div className="flex w-full min-w-0 items-center justify-between gap-2">
                              <span className="min-w-0 truncate">
                                {item.name}
                              </span>
                              <ItemDetail>{item.code}</ItemDetail>
                            </div>
                          </SearchableSelect.MultiItem>
                        )}
                      </SearchableSelect.List>
                    </SearchableSelect.Popover>
                  </SearchableSelect>
                  <Description>
                    Exactly three distinct discriminatory test codes (HIV / HBV
                    / HCV) iTransplant should receive. The combined NAT test
                    cannot be selected here.
                  </Description>
                  {error?.message ? (
                    <FieldError>{error.message}</FieldError>
                  ) : null}
                </div>
              )}
            />

            <Controller
              control={control}
              name="expandWhenValues"
              render={({ field, fieldState: { error } }) => (
                <div className="flex flex-col gap-1">
                  <Label>Expand when result is</Label>
                  <SearchableSelect
                    selectionMode="multiple"
                    selectedKeys={new Set(field.value)}
                    onSelectionChange={(keys) => {
                      if (keys === "all") {
                        field.onChange(resultOptions.map((item) => item.id));
                        return;
                      }
                      field.onChange(
                        Array.from(keys).filter(
                          (key): key is string => typeof key === "string",
                        ),
                      );
                    }}
                  >
                    <SearchableSelect.Button className="justify-between">
                      <SearchableSelect.Value
                        items={resultOptions}
                        getKey={(item) => item.id}
                        getText={(item) => item.value}
                        placeholder="Select result value(s)"
                        className="flex-1 text-start"
                      >
                        {({ selectedItems }) => (
                          <span
                            className="truncate"
                            title={selectedItems
                              .map((item) => item.value)
                              .join(", ")}
                          >
                            {selectedItems.length === 0
                              ? "Select result value(s)"
                              : selectedItems.length === 1
                                ? selectedItems[0]?.value
                                : `${selectedItems.length} selected`}
                          </span>
                        )}
                      </SearchableSelect.Value>
                    </SearchableSelect.Button>
                    <SearchableSelect.Popover>
                      <SearchableSelect.Input placeholder="Search results..." />
                      <SearchableSelect.List
                        shouldFocusOnHover
                        items={resultOptions}
                      >
                        {(item) => {
                          const descriptionDiffers =
                            item.description.trim().toLowerCase() !==
                            item.value.trim().toLowerCase();
                          return (
                            <SearchableSelect.MultiItem
                              id={item.id}
                              textValue={`${item.value} ${item.description}`}
                            >
                              <div className="flex w-full min-w-0 items-center gap-2">
                                <span
                                  className="min-w-0 flex-1 truncate"
                                  title={item.value}
                                >
                                  {item.value}
                                </span>
                                {descriptionDiffers ? (
                                  <ItemDetail className="max-w-[40%] shrink truncate">
                                    {item.description}
                                  </ItemDetail>
                                ) : null}
                              </div>
                            </SearchableSelect.MultiItem>
                          );
                        }}
                      </SearchableSelect.List>
                    </SearchableSelect.Popover>
                  </SearchableSelect>
                  <Description>
                    Non-reactive{" "}
                    <code className="text-foreground">finalResultId</code>{" "}
                    values that synthesize every discrim code
                    {parentRefRange ? (
                      <>
                        {" "}
                        (parent reference range is{" "}
                        <code className="text-foreground">
                          {parentRefRange}
                        </code>
                        )
                      </>
                    ) : null}
                    .
                  </Description>
                  {error?.message ? (
                    <FieldError>{error.message}</FieldError>
                  ) : null}
                </div>
              )}
            />
          </CardContent>
        ) : null}
      </Card>

      <div className="flex justify-end">
        <Button type="submit" isPending={mutation.isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
}
